import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RecomputeTeamChange, getRecomputeTeam } from "../../services/RatingRecomputeService";

// "Team" section of /admin/recompute: one team's players, old → new Rating 3.0 per map and over the season
// (rounds-weighted, like the site's season averages), before or after Apply.

const C = { panel: "#232323", inset: "#1c1c1c", line: "#333", text: "#e2e8f0", muted: "rgba(255,255,255,0.55)", good: "#4ade80", bad: "#f87171" };
const label: React.CSSProperties = { fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 0.6, color: C.muted };
const th: React.CSSProperties = { textAlign: "left", padding: "7px 10px", ...label, borderBottom: "1px solid #2a2a2a", background: C.inset };
const td: React.CSSProperties = { padding: "6px 10px", borderBottom: "1px solid #262626", fontSize: "0.86rem" };
const num: React.CSSProperties = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

const f3 = (v: number | null | undefined) => (v == null ? "–" : v.toFixed(3));
const fSw = (v: number | null | undefined) => (v == null ? "–" : `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`);
const Delta: React.FC<{ d: number | null }> = ({ d }) => (
    <span style={{ color: d == null ? C.muted : d >= 0 ? C.good : C.bad, fontWeight: 700 }}>
        {d == null ? "–" : `${d >= 0 ? "+" : ""}${d.toFixed(3)}`}
    </span>
);

// a team page link or a bare id
const parseTeamId = (s: string): string => s.match(/[a-f0-9]{24}/i)?.[0] ?? s.trim();

interface Total { steamId: string; name: string; maps: number; old: number | null; new: number | null }

const weighted = (rows: RecomputeTeamChange[], pick: (r: RecomputeTeamChange) => number | null): number | null => {
    let s = 0, w = 0;
    for (const r of rows) {
        const v = pick(r);
        if (v == null) continue;
        const k = r.rounds && r.rounds > 0 ? r.rounds : 1;
        s += v * k; w += k;
    }
    return w > 0 ? s / w : null;
};

const RecomputeTeamView: React.FC<{ season: number }> = ({ season }) => {
    const [input, setInput] = useState("");
    const [rows, setRows] = useState<RecomputeTeamChange[] | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = async () => {
        const id = parseTeamId(input);
        if (!id) return;
        setBusy(true); setError(null);
        try { setRows(await getRecomputeTeam(season, id)); }
        catch { setError("Could not load that team."); }
        finally { setBusy(false); }
    };

    const totals = useMemo((): Total[] => {
        if (!rows) return [];
        const by = new Map<string, RecomputeTeamChange[]>();
        for (const r of rows) by.set(r.steamId, [...(by.get(r.steamId) ?? []), r]);
        return Array.from(by.entries()).map(([steamId, rs]) => ({
            steamId, name: rs[0].name || steamId, maps: rs.length,
            old: weighted(rs, r => r.old), new: weighted(rs, r => r.new),
        })).sort((a, b) => ((b.new ?? 0) - (b.old ?? 0)) - ((a.new ?? 0) - (a.old ?? 0)));
    }, [rows]);

    const maps = useMemo(() => {
        if (!rows) return [];
        const order: string[] = [];
        const by = new Map<string, RecomputeTeamChange[]>();
        for (const r of rows) {
            if (!by.has(r.gameId)) { by.set(r.gameId, []); order.push(r.gameId); }
            by.get(r.gameId)!.push(r);
        }
        return order.map(g => by.get(g)!);
    }, [rows]);

    return (
        <div style={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                <span style={label}>Team</span>
                <input id="recompute-team" value={input} placeholder="Paste a team link or id"
                    onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") load(); }}
                    style={{ flex: "1 1 260px", minWidth: 0, background: C.inset, color: C.text, border: `1px solid ${C.line}`, borderRadius: 6, padding: "5px 9px" }} />
                <button onClick={load} disabled={busy || !input.trim()}
                    style={{ cursor: "pointer", borderRadius: 8, padding: "6px 14px", fontWeight: 700, fontSize: "0.85rem", border: `1px solid ${C.line}`, background: "transparent", color: C.text }}>
                    {busy ? "Loading…" : "Show"}
                </button>
                <span style={{ color: C.muted, fontSize: "0.82rem", flexBasis: "100%" }}>
                    Old → new Rating 3.0 for that team's players on every computed map of season {season}. Totals are rounds-weighted, like the season averages on the site.
                </span>
            </div>
            {error && <div style={{ padding: "0 16px 12px", color: C.bad }}>{error}</div>}
            {rows && rows.length === 0 && <div style={{ padding: "0 16px 14px", color: C.muted }}>No computed maps for that team in season {season}.</div>}
            {rows && rows.length > 0 && (
                <div style={{ display: "grid", gap: 0 }}>
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
                            <thead><tr><th style={th}>Player</th><th style={{ ...th, textAlign: "right" }}>Maps</th><th style={{ ...th, textAlign: "right" }}>Season old</th><th style={{ ...th, textAlign: "right" }}>Season new</th><th style={{ ...th, textAlign: "right" }}>Change</th></tr></thead>
                            <tbody>
                                {totals.map(t => (
                                    <tr key={t.steamId}>
                                        <td style={td}><Link to={`/player/${t.steamId}`} style={{ color: C.text, fontWeight: 700 }}>{t.name}</Link></td>
                                        <td style={num}>{t.maps}</td>
                                        <td style={num}>{f3(t.old)}</td>
                                        <td style={num}>{f3(t.new)}</td>
                                        <td style={num}><Delta d={t.old != null && t.new != null ? t.new - t.old : null} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div style={{ padding: "14px 16px 6px", ...label }}>Per map</div>
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                            <thead><tr><th style={th}>Player</th><th style={{ ...th, textAlign: "right" }}>Old</th><th style={{ ...th, textAlign: "right" }}>New</th><th style={{ ...th, textAlign: "right" }}>Change</th><th style={{ ...th, textAlign: "right" }}>Swing old → new</th></tr></thead>
                            <tbody>
                                {maps.map(rs => {
                                    const m = rs[0];
                                    const won = m.teamScore != null && m.oppScore != null ? m.teamScore > m.oppScore : null;
                                    return (
                                        <React.Fragment key={m.gameId}>
                                            <tr>
                                                <td colSpan={5} style={{ ...td, background: C.inset, color: C.muted, fontSize: "0.8rem" }}>
                                                    {m.matchTime ? new Date(m.matchTime + "Z").toLocaleDateString() : ""}{" · "}
                                                    {m.matchId ? <Link to={`/match/${m.matchId}`} style={{ color: "#ee7d3f" }}>{m.mapName ?? m.gameId}</Link> : m.mapName}
                                                    {m.teamScore != null && <> · <b style={{ color: won ? C.good : C.bad }}>{m.teamScore}-{m.oppScore}</b></>}
                                                    {m.status === "applied" && " · applied"}
                                                </td>
                                            </tr>
                                            {rs.map(r => (
                                                <tr key={`${r.gameId}-${r.steamId}`}>
                                                    <td style={td}>{r.name || r.steamId}</td>
                                                    <td style={num}>{f3(r.old)}</td>
                                                    <td style={num}>{f3(r.new)}</td>
                                                    <td style={num}><Delta d={r.old != null && r.new != null ? r.new - r.old : null} /></td>
                                                    <td style={{ ...num, color: C.muted }}>{fSw(r.oldSwing)} → {fSw(r.newSwing)}</td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecomputeTeamView;
